import React from 'react';
import ReactDOM from 'react-dom/client';

// Polyfill crypto for environments that don't support it
// (required by Klever extension's inject.js during web.initialize())
if (typeof (globalThis as any).crypto === 'undefined') {
  (globalThis as any).crypto = {};
}
if (!(globalThis as any).crypto.getRandomValues) {
  // Fallback using Math.random — sufficient for nonce generation, not for key material
  (globalThis as any).crypto.getRandomValues = (buf: Uint8Array) => {
    for (let i = 0; i < buf.length; i++) buf[i] = Math.floor(Math.random() * 256);
    return buf;
  };
}
if (!crypto.randomUUID) {
  (crypto as any).randomUUID = (): string => {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return [...bytes].map((b, i) =>
      ([4, 6, 8, 10].includes(i) ? '-' : '') + b.toString(16).padStart(2, '0')
    ).join('');
  };
}

// Suppress Klever SDK "No Listener" tab-messaging noise when extension is not installed
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.message?.includes('No Listener')) {
    event.preventDefault();
  }
});
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import './app/globals.scss';
import RootLayout from './app/layout';
import reportWebVitals from './reportWebVitals';
import { store } from './redux/Store';
import { AuthProvider } from './components';
import { SocketProvider } from './contexts/SocketContext';
import MaintenancePage from './components/MaintenancePage/MaintenancePage';

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element with id "root" not found.');
}

const root = ReactDOM.createRoot(container);

// Maintenance-mode short-circuit. Baked in at build time via
// VITE_MAINTENANCE_MODE — flip the flag + redeploy the frontend to
// swap the entire SPA for a static status page. Renders BEFORE any
// provider so no API call, socket connect, or wallet init runs during
// scheduled downtime. See docs/RUNBOOK.md § Maintenance mode. (#677)
const MAINTENANCE_MODE = import.meta.env.VITE_MAINTENANCE_MODE === 'true';

root.render(
  MAINTENANCE_MODE ? (
    <React.StrictMode>
      <MaintenancePage />
    </React.StrictMode>
  ) : (
    <React.StrictMode>
      <Provider store={store}>
        <AuthProvider>
          <SocketProvider>
            <BrowserRouter>
              <RootLayout />
            </BrowserRouter>
          </SocketProvider>
        </AuthProvider>
      </Provider>
    </React.StrictMode>
  )
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
